import React, { useEffect, useMemo, useState } from "react";
import {
  Bug,
  CheckCircle2,
  Eye,
  ExternalLink,
  Lightbulb,
  Mail,
  MessageSquareWarning,
  RefreshCw,
  Search,
  Send,
  X,
} from "lucide-react";
import { bugReportsAPI } from "../../../services/api";

const TYPE_META = {
  bug: {
    label: "Bug",
    icon: Bug,
    className: "bg-red-50 text-red-700 border-red-200",
  },
  issue: {
    label: "Issue",
    icon: MessageSquareWarning,
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  feature_request: {
    label: "Feature Request",
    icon: Lightbulb,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
};

const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.issue;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold ${meta.className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
};

function ResolutionModal({ report, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!report) return;
    setSubject(`SmokyQ Update — ${report.report_number}`);
    setMessage(
      `Hello ${report.reporter_name || ""},\n\nThe item you reported has been addressed. Please refresh SmokyQ and verify the fix.\n\nThank you for helping us improve the system.`,
    );
    setError("");
  }, [report]);

  if (!report) return null;

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      setError("Subject and message are required.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const response = await bugReportsAPI.sendResolutionEmail(report.id, {
        subject: subject.trim(),
        message: message.trim(),
      });
      onSent(response?.data || {});
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send the email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Send Resolution Email
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              To: {report.reporter_email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Subject
            </span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Message
            </span>
            <textarea
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
            />
          </label>
          {error ? (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={sending}
            onClick={send}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportDrawer({ report, onClose, onSendEmail }) {
  if (!report) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/30" onMouseDown={onClose}>
      <aside
        className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b bg-slate-950 px-5 py-4 text-white">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              User report
            </div>
            <h2 className="mt-1 text-xl font-bold">{report.report_number}</h2>
            <p className="mt-1 text-sm text-slate-300">{report.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className="flex flex-wrap items-center gap-2">
            <TypeBadge type={report.report_type} />
            {report.resolution_email_sent_at ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Email sent
              </span>
            ) : (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
                No resolution email sent
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 rounded-xl border bg-gray-50 p-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-semibold uppercase text-gray-500">
                Reporter
              </div>
              <div className="mt-1 text-sm font-medium text-gray-900">
                {report.reporter_name || "-"}
              </div>
              <div className="text-xs text-gray-500">
                {report.reporter_email || "-"}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase text-gray-500">
                Project
              </div>
              <div className="mt-1 text-sm text-gray-900">
                {report.project_name || "Global / no project"}
              </div>
              <div className="text-xs text-gray-500">
                {report.project_role?.replaceAll("_", " ") || "-"}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase text-gray-500">
                Submitted
              </div>
              <div className="mt-1 text-sm text-gray-900">
                {fmtDate(report.created_at)}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase text-gray-500">
                System role
              </div>
              <div className="mt-1 text-sm text-gray-900">
                {report.system_role?.replaceAll("_", " ") || "-"}
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4">
            <h3 className="font-semibold text-gray-900">Description</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">
              {report.description}
            </p>
          </div>

          <div className="rounded-xl border p-4">
            <h3 className="font-semibold text-gray-900">Technical context</h3>
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-600">Page:</span>{" "}
                <span className="break-all text-gray-800">
                  {report.page_url || "-"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Screen:</span>{" "}
                {report.screen_size || "-"}
              </div>
              <div>
                <span className="font-medium text-gray-600">Browser:</span>{" "}
                <span className="break-words text-gray-700">
                  {report.browser_info || "-"}
                </span>
              </div>
            </div>
          </div>

          {report.screenshot_url ? (
            <div className="rounded-xl border p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Screenshot</h3>
                <a
                  href={report.screenshot_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline"
                >
                  Open full size <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
              <img
                src={report.screenshot_url}
                alt="User report screenshot"
                className="max-h-96 w-full rounded-lg border bg-gray-50 object-contain"
              />
            </div>
          ) : null}
        </div>

        <div className="border-t bg-white p-4">
          <button
            type="button"
            onClick={onSendEmail}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
          >
            <Mail className="h-4 w-4" />
            {report.resolution_email_sent_at
              ? "Send Another Resolution Email"
              : "Send Resolution Email"}
          </button>
        </div>
      </aside>
    </div>
  );
}

const BugReportsAdminView = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [resolutionReport, setResolutionReport] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await bugReportsAPI.adminList();
      setReports(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load user reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter((report) => {
      if (typeFilter !== "all" && report.report_type !== typeFilter)
        return false;
      if (!q) return true;
      return [
        report.report_number,
        report.title,
        report.description,
        report.reporter_name,
        report.reporter_email,
        report.project_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [reports, query, typeFilter]);

  const counts = useMemo(
    () => ({
      total: reports.length,
      bugs: reports.filter((r) => r.report_type === "bug").length,
      issues: reports.filter((r) => r.report_type === "issue").length,
      features: reports.filter((r) => r.report_type === "feature_request")
        .length,
    }),
    [reports],
  );

  const onResolutionSent = async () => {
    setResolutionReport(null);
    await load();
    if (selected) {
      try {
        const response = await bugReportsAPI.adminGetOne(selected.id);
        setSelected(response?.data || null);
      } catch {
        setSelected(null);
      }
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bug Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            User-submitted bugs, issues, and feature requests.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 self-start rounded-lg border bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{" "}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ["Total", counts.total],
          ["Bugs", counts.bugs],
          ["Issues", counts.issues],
          ["Feature Requests", counts.features],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search report, title, user, email, or project..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-500"
          >
            <option value="all">All types</option>
            <option value="bug">Bugs</option>
            <option value="issue">Issues</option>
            <option value="feature_request">Feature Requests</option>
          </select>
        </div>

        {error ? (
          <div className="m-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Report</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Loading reports...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No reports found.
                  </td>
                </tr>
              ) : (
                filtered.map((report) => (
                  <tr key={report.id} className="hover:bg-orange-50/40">
                    <td className="whitespace-nowrap px-4 py-3 font-semibold text-gray-900">
                      {report.report_number}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <TypeBadge type={report.report_type} />
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <div
                        className="truncate font-medium text-gray-900"
                        title={report.title}
                      >
                        {report.title}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">
                        {report.reporter_name || "-"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {report.reporter_email || "-"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {report.project_name || "Global"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                      {fmtDate(report.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {report.resolution_email_sent_at ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Sent
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not sent</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setSelected(report)}
                        className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ReportDrawer
        report={selected}
        onClose={() => setSelected(null)}
        onSendEmail={() => setResolutionReport(selected)}
      />
      <ResolutionModal
        report={resolutionReport}
        onClose={() => setResolutionReport(null)}
        onSent={onResolutionSent}
      />
    </div>
  );
};

export default BugReportsAdminView;
